import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ImageBackground,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth, CONSENT_PROMPT_SKIP_KEY } from '@/contexts/AuthContext';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import PrimaryButton from '@/components/PrimaryButton';
import OneSignalService from '@/services/oneSignalService';
import { apiService, refreshAccessToken } from '@/services/apiService';
import { TempOnboardingService } from '@/services/tempOnboardingService';

export default function OnboardingNotificationsScreen() {
  const { logCompleted } = useOnboardingStepTracking('notifications');
  const router = useRouter();
  const { t } = useLanguage();
  const { user, refreshProfile } = useAuth();
  const insets = useSafeAreaInsets();
  const [isLoading, setIsLoading] = useState(false);

  // Durable Google-funnel detection: derive it from the authenticated session
  // provider (restored on cold start) rather than in-memory state, so killing
  // the app mid-funnel doesn't drop the user into the email flow on relaunch.
  const isGoogleUser = user?.app_metadata?.provider === 'google';

  // PRD_GOOGLE_AUTH §3 — for a Google user the account already exists, so we
  // skip first-access / verify-code / set-password and complete the profile
  // directly via the authenticated endpoint, then go to the success screen.
  const completeGoogleOnboarding = async () => {
    const tempData = await TempOnboardingService.getData();
    const onboardingData = {
      name: tempData.name,
      interests: tempData.interests,
      gender: tempData.gender,
      support_recipient: tempData.supportRecipient,
      sponsorship_preference: tempData.sponsorshipPreference,
      addiction_types: tempData.addictionTypes,
      addiction_frequency: tempData.addictionFrequency,
      app_goal: tempData.appGoal,
    };

    const result = await apiService.post('/api/auth/google/complete-profile', { onboardingData });
    if (!result.success) {
      console.error('❌ complete-profile failed:', result.error, result.status);
      throw new Error(`complete-profile failed: ${result.error ?? result.status ?? 'unknown'}`);
    }

    // Google users accept the Terms of Use & Privacy Policy on the Google
    // OAuth consent screen, and the server records that acceptance in
    // /api/auth/google/complete-profile. Suppress the post-login consent sheet
    // for this fresh signup the same way the email flow does: set the one-shot
    // skip flag BEFORE refreshProfile, so the startup prompt queue (rebuilt as
    // soon as the profile loads) doesn't race ahead and pop the sheet.
    try {
      await AsyncStorage.setItem(CONSENT_PROMPT_SKIP_KEY, 'true');
    } catch (skipError) {
      console.warn('⚠️ Failed to set consent skip flag (non-blocking):', skipError);
    }

    try {
      await refreshProfile();
    } catch (refreshError) {
      console.warn('⚠️ refreshProfile after Google complete-profile failed:', refreshError);
    }

    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) => logClarityEvent('google_signup_completed', {}))
      .catch(() => {});

    router.replace('/onboarding-success');
  };

  const handleAllowNotifications = async () => {
    setIsLoading(true);
    try {
      console.log('🔔 User requesting notification permission...');
      const granted = await OneSignalService.requestPermission();
      console.log('📱 Notification permission result:', granted);

      // PRD §5.2 — `onboarding_step_completed` for the `notifications`
      // step. Fired regardless of grant outcome because forward
      // navigation always proceeds; the OS-level permission state is
      // captured separately in OneSignal/system metrics.
      logCompleted();

      if (isGoogleUser) {
        // Force-refresh the in-memory JWT before completing the Google profile.
        // Guards against a stale token from a previously-authenticated device
        // account: tokenState may hold the old user's valid-but-wrong JWT, which
        // the server rejects (non-Google user check → 403 → shows Google error).
        await refreshAccessToken(true).catch((e) =>
          console.warn('⚠️ Pre-completion token refresh failed (non-blocking):', e?.message)
        );
        await completeGoogleOnboarding();
        return;
      }

      // Navigate to email step regardless of permission result
      // (User may grant, deny, or have already set permissions previously)
      router.push('/first-access');
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      // For the Google path, a failed completion should NOT silently drop the
      // user into the email flow — surface the error and let them retry.
      if (isGoogleUser) {
        Alert.alert(t('welcome.google.errorTitle'), t('welcome.google.errorMessage'));
        setIsLoading(false);
        return;
      }
      // Still navigate forward even if there's an error
      router.push('/first-access');
    } finally {
      setIsLoading(false);
    }
  };

  const progressWidth = 88; // Step 9 of 9

  return (
    <ImageBackground
      source={require('@/assets/images/onboarding-push.png')}
      style={styles.fullScreenBackground}
      resizeMode="cover"
    >
      <OnboardingLayout progressWidth={progressWidth} transparentHeader={true}>
        <View style={styles.contentContainer}>
          {/* Text Section */}
          <View style={styles.textSection}>
            <Text style={styles.title}>
              {t('onboarding.notifications.title')}
            </Text>
            <Text style={styles.subtitle}>
              {t('onboarding.notifications.subtitle')}
            </Text>
          </View>

          {/* Button Section */}
          <View style={[styles.buttonSection, { paddingBottom: insets.bottom + 24 }]}>
            <PrimaryButton
              title={t('onboarding.notifications.button')}
              onPress={handleAllowNotifications}
              disabled={isLoading}
              loading={isLoading}
              style={styles.primaryButton}
            />
          </View>
        </View>
      </OnboardingLayout>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  fullScreenBackground: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  textSection: {
    alignItems: 'center',
    gap: 24,
    marginBottom: 24,
    paddingHorizontal: 40,
  },
  title: {
    color: '#015480',
    textAlign: 'center',
    fontFamily: 'InstrumentSerif_400Regular',
    fontSize: 40,
    fontWeight: '400',
    lineHeight: 44,
    width: 345,
  },
  titleRegular: {
    fontFamily: 'InstrumentSerif_400Regular',
    fontStyle: 'normal',
  },
  titleItalic: {
    fontFamily: 'InstrumentSerif_400Regular_Italic',
    fontStyle: 'italic',
  },
  subtitle: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    width: 345,
  },
  buttonSection: {
    width: '100%',
    gap: 8,
    paddingHorizontal: 24,
  },
  primaryButton: {
    backgroundColor: '#151515',
  },
});
